/**
 * Hospital dashboard statistics.
 *
 * Primary path: one request to the HMIS backend (`GET /api/analytics/hospital-stats`),
 * which does the counting next to Medplum with the caller's own token. Fallback, only
 * if that route does not exist yet (older backend): the original browser-side counting
 * below, which pages through Medplum with the logged-in user's token. Either way the
 * token is project-scoped, so only the user's own hospital is ever returned.
 */

import { apiClient } from '@/lib/api/client';
import { caseSheetSummaryApi } from '@/lib/api/case-sheet-summary';
import { useAuthStore } from '@/lib/stores/authStore';
import {
  buildTimeline,
  countConsultations,
  countDistinct,
  isInstantInRange,
  isLinkedToAppointmentIn,
  ON_HOLD_APPOINTMENT_STATUSES,
  patientIdFromRef,
  rangeToUtcBounds,
  type ConsultationCounts,
  type DateRange,
  type EncounterLike,
  type TimelinePoint,
} from '@/lib/utils/hospitalStatsCalc';

const PAGE_SIZE = 1000;
const MAX_PAGES = 30; // safety cap: 30k resources per search
const CASE_SHEET_CONCURRENCY = 6; // parallel case-sheet summary lookups
/** Appointment statuses counted on the dashboard. */
const APPOINTMENT_STATUSES = ['booked', 'arrived', 'checked-in', 'fulfilled'] as const;

interface Bundle<R> {
  entry?: Array<{ resource?: R }>;
}

interface AppointmentRes {
  id?: string;
  status?: string;
  start?: string;
  participant?: Array<{ actor?: { reference?: string } }>;
}

interface CompositionRes {
  id?: string;
  date?: string;
  subject?: { reference?: string };
}

interface ObservationRes {
  effectiveDateTime?: string;
  issued?: string;
  subject?: { reference?: string };
}

export interface HospitalStats {
  range: DateRange;
  /** Appointments with status booked, arrived, checked-in or fulfilled */
  appointments: number;
  /** Distinct patients across everything counted below */
  uniquePatients: number;
  /**
   * Distinct patients with a fulfilled appointment, a finished (Completed) consultation, or an
   * in-progress consultation whose appointment is booked, arrived, checked-in or fulfilled
   */
  completedPatients: number;
  /** Compositions (successful PA / SOAP notes) */
  pa: number;
  /** Every vital-sign Observation (one per reading), not grouped by patient */
  vitals: number;
  consultations: Pick<ConsultationCounts, 'total' | 'completed' | 'onHold' | 'notSaved'>;
  timeline: TimelinePoint[];
}

/** Run a search and follow it with _offset paging until a short page comes back. */
async function searchAll<R>(resource: string, params: Array<[string, string]>): Promise<R[]> {
  const all: R[] = [];
  for (let page = 0; page < MAX_PAGES; page++) {
    const query = new URLSearchParams(params);
    query.set('_count', String(PAGE_SIZE));
    query.set('_offset', String(page * PAGE_SIZE));
    const response = await apiClient.get<Bundle<R>>(`/${resource}?${query.toString()}`);
    const resources = (response.data.entry ?? []).map((e) => e.resource).filter((r): r is R => !!r);
    all.push(...resources);
    if (resources.length < PAGE_SIZE) break;
    // A full final page means there may be more records we are not reading.
    // Fail loudly instead of returning a silently truncated (wrong) result.
    if (page === MAX_PAGES - 1) {
      throw new Error(
        `Search for ${resource} exceeded ${MAX_PAGES} pages of ${PAGE_SIZE} records. Choose a shorter date range.`
      );
    }
  }
  return all;
}

function dateParams(name: string, start: string, end: string): Array<[string, string]> {
  return [
    [name, `ge${start}`],
    [name, `le${end}`],
  ];
}

/**
 * Encounter ids (from a list) whose doctor reached the case sheet. The consultation
 * page saves a case-sheet summary right before navigating there; the On Hold button
 * only saves a draft. `getSummary` returns null for a real "not found" (404). Any other
 * failure (server error, expired token) is NOT treated as "not reached": it propagates so
 * the page shows an error instead of plausible but wrong On hold / Not saved numbers.
 */
async function findEncounterIdsWithCaseSheet(encounterIds: string[]): Promise<Set<string>> {
  const reached = new Set<string>();
  for (let i = 0; i < encounterIds.length; i += CASE_SHEET_CONCURRENCY) {
    const batch = encounterIds.slice(i, i + CASE_SHEET_CONCURRENCY);
    const results = await Promise.all(
      batch.map(async (id) => ((await caseSheetSummaryApi.getSummary(id)) ? id : null))
    );
    for (const id of results) if (id) reached.add(id);
  }
  return reached;
}

/**
 * One search per status (Medplum is unreliable with comma-separated status),
 * merged and de-duplicated by Appointment id.
 */
async function searchAppointments(start: string, end: string): Promise<AppointmentRes[]> {
  const perStatus = await Promise.all(
    APPOINTMENT_STATUSES.map((status) =>
      searchAll<AppointmentRes>('Appointment', [
        ['status', status],
        ...dateParams('date', start, end),
      ])
    )
  );
  const byId = new Map<string, AppointmentRes>();
  for (const appointment of perStatus.flat()) {
    byId.set(appointment.id ?? `no-id-${byId.size}`, appointment);
  }
  return Array.from(byId.values());
}

/** Thrown when the backend does not have the stats route yet (older deployment). */
class StatsEndpointMissingError extends Error {}

/** One request to the backend, which does all the counting. */
async function fetchHospitalStatsFromBackend(
  range: DateRange,
  signal?: AbortSignal
): Promise<HospitalStats> {
  const token = useAuthStore.getState().token;
  const query = new URLSearchParams({ from: range.from, to: range.to });
  const response = await fetch(
    `${import.meta.env.VITE_AUTH_API_URL}/api/analytics/hospital-stats?${query.toString()}`,
    {
      method: 'GET',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      ...(signal && { signal }),
    }
  );

  if (response.status === 404) throw new StatsEndpointMissingError();
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Failed to load hospital statistics (${response.status})`);
  }
  const body = (await response.json()) as { stats: HospitalStats };
  return body.stats;
}

/**
 * Hospital statistics for an IST date range. Uses the backend endpoint; if the backend
 * does not have it yet, falls back to counting in the browser.
 */
export async function fetchHospitalStats(
  range: DateRange,
  signal?: AbortSignal
): Promise<HospitalStats> {
  try {
    return await fetchHospitalStatsFromBackend(range, signal);
  } catch (error) {
    if (error instanceof StatsEndpointMissingError) {
      return fetchHospitalStatsInBrowser(range);
    }
    throw error;
  }
}

/** Original browser-side implementation, kept as a fallback for older backends. */
async function fetchHospitalStatsInBrowser(range: DateRange): Promise<HospitalStats> {
  const { start, end } = rangeToUtcBounds(range);

  const [appointments, encounterResults, compositions, observations] = await Promise.all([
    searchAppointments(start, end),
    searchAll<EncounterLike>('Encounter', [...dateParams('date', start, end)]),
    searchAll<CompositionRes>('Composition', [...dateParams('date', start, end)]),
    searchAll<ObservationRes>('Observation', [
      ['category', 'vital-signs'],
      ...dateParams('date', start, end),
      ['_elements', 'subject,effectiveDateTime,issued'],
    ]),
  ]);

  // An Encounter date search matches any period that OVERLAPS the range, so an old visit
  // that is still in-progress (no end date) would match every later range. Keep only the
  // visits that started inside the range, the same bounds the chart uses.
  const encounters = encounterResults.filter((e) => isInstantInRange(e.period?.start, range));

  // Consultations: completed (finished) + on hold + not saved (see countConsultations).
  const onHoldAppointmentIds = new Set(
    appointments
      .filter((a) => a.id && ON_HOLD_APPOINTMENT_STATUSES.includes(a.status ?? ''))
      .map((a) => a.id as string)
  );
  const inProgressIds = encounters
    .filter((e) => e.status === 'in-progress' && e.id)
    .map((e) => e.id as string);
  const withCaseSheet = await findEncounterIdsWithCaseSheet(inProgressIds);
  const consultations = countConsultations(encounters, onHoldAppointmentIds, withCaseSheet);

  const appointmentPatientId = (a: AppointmentRes) =>
    patientIdFromRef(
      a.participant?.find((p) => p.actor?.reference?.startsWith('Patient/'))?.actor?.reference
    );
  const appointmentPatients = appointments.map(appointmentPatientId);
  const consultationPatients = consultations.encounters.map((e) =>
    patientIdFromRef(e.subject?.reference)
  );
  // Patients who completed: a fulfilled appointment, OR a finished (Completed) consultation,
  // OR a consultation still in progress whose appointment has any counted status
  // (booked, arrived, checked-in or fulfilled).
  const countedAppointmentIds = new Set(
    appointments.filter((a) => a.id).map((a) => a.id as string)
  );
  const completedPatients = countDistinct([
    ...appointments.filter((a) => a.status === 'fulfilled').map(appointmentPatientId),
    ...consultations.encounters
      .filter((e) => e.status === 'finished')
      .map((e) => patientIdFromRef(e.subject?.reference)),
    ...encounters
      .filter(
        (e) => e.status === 'in-progress' && isLinkedToAppointmentIn(e, countedAppointmentIds)
      )
      .map((e) => patientIdFromRef(e.subject?.reference)),
  ]);
  const paPatients = compositions.map((c) => patientIdFromRef(c.subject?.reference));
  const vitalsPatientIds = observations.map((o) => patientIdFromRef(o.subject?.reference));

  const timeline = buildTimeline(range, {
    appointments: appointments.map((a) => a.start),
    consultations: consultations.encounters.map((e) => e.period?.start),
    pa: compositions.map((c) => c.date),
    vitals: observations.map((o) => o.effectiveDateTime ?? o.issued),
  });

  return {
    range,
    appointments: appointments.length,
    uniquePatients: countDistinct([
      ...appointmentPatients,
      ...consultationPatients,
      ...paPatients,
      ...vitalsPatientIds,
    ]),
    completedPatients,
    pa: compositions.length,
    vitals: observations.length,
    consultations: {
      total: consultations.total,
      completed: consultations.completed,
      onHold: consultations.onHold,
      notSaved: consultations.notSaved,
    },
    timeline,
  };
}
